/**
 * MODULE: _shared/frontend/utils/id.ts
 * PURPOSE: safeRandomUUID — RFC4122 v4 when crypto.randomUUID exists; fallback otherwise.
 * RECENT: 2026-06-08 [2.2.1335] — fixes TypeError on HTTP hosts (page editor blockConversions).
 */
export function safeRandomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Generates a unique ID using crypto.randomUUID() with fallback for older browsers
 * @param prefix - Optional prefix to prepend to the ID
 * @returns A unique identifier string
 * @example
 * generateId() // "550e8400-e29b-41d4-a716-446655440000"
 * generateId('user') // "user_550e8400-e29b-41d4-a716-446655440000"
 */
export function generateId(prefix?: string): string {
  const id = safeRandomUUID();
  return prefix ? `${prefix}_${id}` : id;
}
